import { AlertPreference, SavedItem, SavedItemOwnerType, SavedPrice, SavedSearchContext, UserSavedItem } from './domain';
import { anonymousSessionIdFromUserId, anonymousSessionUserId } from './anonymous-session';
import {
    SavedItemAlertPreferenceUpdate,
    SavedItemAnonymousMergeParams,
    SavedItemRepository,
    SavedItemRepositoryIdentity,
    SavedItemRepositoryListParams,
    SavedItemRepositorySaveInput,
    SavedItemRepositoryScope
} from './saved-item-repository';
import { lookitUserSavedItems } from './mock-data';

export interface LookitStorage {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
}

export interface SavedItemListParams extends SavedItemRepositoryListParams {}

export interface SavedItemIdentity extends SavedItemRepositoryIdentity {}

export interface SavedItemNoteUpdate extends SavedItemIdentity {
    note: string | null;
}

export interface SavedItemAlertPreferenceInput extends SavedItemAlertPreferenceUpdate {}

export interface SavedItemAdapter extends SavedItemRepository {
    list(params?: SavedItemListParams): Promise<UserSavedItem[]>;
    save(item: SavedItemRepositorySaveInput, params?: SavedItemRepositoryScope): Promise<UserSavedItem>;
    remove(identity: SavedItemIdentity, params?: SavedItemRepositoryScope): Promise<UserSavedItem[]>;
    listSavedItems(params?: SavedItemListParams): Promise<UserSavedItem[]>;
    saveItem(item: SavedItemRepositorySaveInput): Promise<UserSavedItem>;
    removeSavedItem(identity: SavedItemIdentity): Promise<UserSavedItem[]>;
    updateAlertPreference(input: SavedItemAlertPreferenceInput, params?: SavedItemRepositoryScope): Promise<UserSavedItem | null>;
    updateSavedItemNote(input: SavedItemNoteUpdate): Promise<UserSavedItem | null>;
    replaceSavedItems(items: UserSavedItem[], params?: SavedItemRepositoryScope): Promise<UserSavedItem[]>;
}

export interface LocalStorageSavedItemAdapterOptions {
    storageKey?: string;
    legacyStorageKeys?: string[];
    storage?: LookitStorage | null;
    fallbackItems?: UserSavedItem[];
}

export interface SavedItemServiceOptions extends LocalStorageSavedItemAdapterOptions, SavedItemListParams {
    adapter?: SavedItemAdapter | null;
}

export const savedItemsStorageKey = 'lookit.saved-items.v2';
export const savedItemsLegacyStorageKeys = ['lookit.saved-items.v1', 'lookit.saved-items'];

function cloneData<T>(data: T): T {
    return JSON.parse(JSON.stringify(data)) as T;
}

function browserStorage(storage?: LookitStorage | null): LookitStorage | null {
    if (storage !== undefined) return storage;
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
}

function parseStoredArray<T extends object>(rawValue: string | null): T[] | null {
    if (rawValue === null) return null;

    try {
        const parsed = JSON.parse(rawValue);
        return Array.isArray(parsed) ? parsed : null;
    } catch (error) {
        return null;
    }
}

function readLocalArray<T extends object>(
    storageKey: string,
    fallback: T[],
    storage?: LookitStorage | null,
    legacyStorageKeys: string[] = []
): T[] {
    const fallbackItems = cloneData(fallback);
    const resolvedStorage = browserStorage(storage);

    if (!resolvedStorage) return fallbackItems;

    const currentItems = parseStoredArray<T>(resolvedStorage.getItem(storageKey));
    if (currentItems) return currentItems;

    for (const legacyKey of legacyStorageKeys) {
        const legacyItems = parseStoredArray<T>(resolvedStorage.getItem(legacyKey));
        if (legacyItems) {
            resolvedStorage.setItem(storageKey, JSON.stringify(legacyItems));
            return legacyItems;
        }
    }

    resolvedStorage.setItem(storageKey, JSON.stringify(fallbackItems));
    return fallbackItems;
}

function numberOrNull(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function stringOrNull(value: unknown): string | null {
    return typeof value === 'string' && value.length > 0 ? value : null;
}

function savedItemUserId(identity: SavedItemRepositoryScope | SavedItemIdentity | UserSavedItem): string | null {
    return stringOrNull('user_id' in identity ? identity.user_id : null) ||
        stringOrNull('userId' in identity ? identity.userId : null);
}

function savedItemAnonymousSessionId(identity: SavedItemRepositoryScope | SavedItemIdentity | UserSavedItem): string | null {
    return stringOrNull('anonymous_session_id' in identity ? identity.anonymous_session_id : null) ||
        stringOrNull('anonymousSessionId' in identity ? identity.anonymousSessionId : null) ||
        anonymousSessionIdFromUserId(savedItemUserId(identity));
}

function savedItemOwnerType(identity: SavedItemRepositoryScope | UserSavedItem): SavedItemOwnerType | null {
    return ('owner_type' in identity ? identity.owner_type : null) ||
        ('ownerType' in identity ? identity.ownerType : null) ||
        null;
}

function scopedUserId(scope?: SavedItemRepositoryScope | null): string | null {
    if (!scope) return null;
    const userId = savedItemUserId(scope);
    const anonymousSessionId = savedItemAnonymousSessionId(scope);
    return userId || (anonymousSessionId ? anonymousSessionUserId(anonymousSessionId) : null);
}

function hasSavedItemScope(scope?: SavedItemRepositoryScope | null): boolean {
    return Boolean(scope && (savedItemUserId(scope) || savedItemAnonymousSessionId(scope)));
}

export function savedItemIdentityKey(identity: SavedItemIdentity | UserSavedItem): string {
    const productClusterId = ('product_cluster_id' in identity ? identity.product_cluster_id : identity.productClusterId) || '';
    const listingId = ('listing_id' in identity ? identity.listing_id : identity.listingId) || null;
    const listingOptionId = ('listing_option_id' in identity ? identity.listing_option_id : identity.listingOptionId) || null;

    return `${productClusterId}::${listingId || 'cluster'}::${listingOptionId || 'all-options'}`;
}

function matchesSavedItemScope(item: UserSavedItem, scope?: SavedItemRepositoryScope | null): boolean {
    if (!scope || !hasSavedItemScope(scope)) return true;

    const targetUserId = savedItemUserId(scope);
    if (targetUserId && item.user_id !== targetUserId) return false;

    const targetAnonymousSessionId = savedItemAnonymousSessionId(scope);
    if (targetAnonymousSessionId) {
        const itemAnonymousSessionId = item.anonymous_session_id || anonymousSessionIdFromUserId(item.user_id);
        const itemAnonymousUserId = anonymousSessionUserId(targetAnonymousSessionId);
        if (itemAnonymousSessionId !== targetAnonymousSessionId && item.user_id !== itemAnonymousUserId) return false;
    }

    return true;
}

function matchesSavedItemIdentity(item: UserSavedItem, identity: SavedItemIdentity): boolean {
    if (!matchesSavedItemScope(item, identity)) return false;
    if (identity.id) return item.id === identity.id;
    if (!identity.productClusterId && !identity.product_cluster_id) return false;
    return savedItemIdentityKey(item) === savedItemIdentityKey(identity);
}

function normalizedSearchContext(item: Partial<UserSavedItem>): SavedSearchContext {
    const context = item.search_context || item.source_context || null;

    return {
        source_request_id: item.source_request_id ?? context?.source_request_id ?? null,
        selected_category_id: item.selected_category_id ?? context?.selected_category_id ?? null,
        selected_category_label: item.selected_category_label ?? context?.selected_category_label ?? null,
        selected_item_label: item.selected_item_label ?? context?.selected_item_label ?? null,
        selected_region_label: item.selected_region_label ?? context?.selected_region_label ?? null,
        selected_region: item.selected_region ?? context?.selected_region ?? null,
        selected_crop: context?.selected_crop ?? null,
        crop_image_id: item.crop_image_id ?? context?.crop_image_id ?? context?.selected_crop?.crop_image_id ?? null,
        original_image_size: item.original_image_size ?? context?.original_image_size ?? null,
        displayed_image_size: item.displayed_image_size ?? context?.displayed_image_size ?? null,
        crop_region_original: item.crop_region_original ?? context?.crop_region_original ?? null,
        crop_preview_url: item.crop_preview_url ?? context?.crop_preview_url ?? null,
        local_crop_id: item.local_crop_id ?? context?.local_crop_id ?? null
    };
}

function normalizedSavedPrice(
    item: Partial<UserSavedItem>,
    savedPrice: number,
    savedAt: string,
    listingId: string | null,
    listingOptionId: string | null
): SavedPrice {
    const snapshot = item.saved_price_snapshot || null;
    const basis: SavedPrice['basis'] = snapshot?.basis ||
        (listingOptionId && savedPrice <= 0 ? 'restock_only' : 'public_effective_price');

    return {
        amount: numberOrNull(snapshot?.amount) ?? savedPrice,
        currency: snapshot?.currency || 'KRW',
        basis,
        listing_id: snapshot?.listing_id ?? listingId,
        listing_option_id: snapshot?.listing_option_id ?? listingOptionId,
        listed_price: numberOrNull(snapshot?.listed_price),
        instant_discount: numberOrNull(snapshot?.instant_discount),
        public_coupon_discount: numberOrNull(snapshot?.public_coupon_discount),
        shipping_fee: numberOrNull(snapshot?.shipping_fee),
        free_shipping_condition: snapshot?.free_shipping_condition ?? null,
        checked_at: snapshot?.checked_at ?? null,
        saved_at: snapshot?.saved_at || savedAt
    };
}

function normalizeSavedItem(item: Partial<UserSavedItem>, scope: SavedItemRepositoryScope = {}): UserSavedItem {
    const createdAt = item.created_at || new Date().toISOString();
    const listingId = item.listing_id || null;
    const listingOptionId = item.listing_option_id || null;
    const savedPrice = numberOrNull(item.saved_price) ?? numberOrNull(item.saved_price_snapshot?.amount) ?? 0;
    const searchContext = normalizedSearchContext(item);
    const savedAt = item.saved_at || item.saved_price_snapshot?.saved_at || createdAt;
    const anonymousSessionId = savedItemAnonymousSessionId(scope) || item.anonymous_session_id;
    const resolvedUserId = scopedUserId(scope) || item.user_id || 'user-demo';
    const inferredAnonymousSessionId = anonymousSessionId || anonymousSessionIdFromUserId(resolvedUserId);
    const ownerType: SavedItemOwnerType = item.owner_type ||
        savedItemOwnerType(scope) ||
        (inferredAnonymousSessionId ? 'anonymous' : 'user');
    const savedPriceSnapshot = normalizedSavedPrice(item, savedPrice, savedAt, listingId, listingOptionId);

    return {
        id: item.id || `saved-local-${Date.now()}-${item.product_cluster_id || 'unknown'}`,
        user_id: resolvedUserId,
        owner_type: ownerType,
        anonymous_session_id: ownerType === 'anonymous' ? inferredAnonymousSessionId : null,
        product_cluster_id: item.product_cluster_id || '',
        listing_id: listingId,
        listing_option_id: listingOptionId,
        product_name: item.product_name || null,
        image_url: item.image_url || null,
        target_color: item.target_color || null,
        target_size: item.target_size || null,
        selected_category_id: searchContext.selected_category_id,
        selected_category_label: searchContext.selected_category_label,
        selected_item_label: searchContext.selected_item_label,
        selected_region_label: searchContext.selected_region_label,
        selected_region: searchContext.selected_region,
        crop_image_id: searchContext.crop_image_id,
        original_image_size: searchContext.original_image_size,
        displayed_image_size: searchContext.displayed_image_size,
        crop_region_original: searchContext.crop_region_original,
        crop_preview_url: searchContext.crop_preview_url,
        local_crop_id: searchContext.local_crop_id,
        source_request_id: searchContext.source_request_id,
        saved_price: savedPriceSnapshot.amount,
        saved_price_snapshot: savedPriceSnapshot,
        search_context: searchContext,
        source_context: searchContext,
        alert_preference: item.alert_preference || null,
        note: item.note || null,
        saved_at: savedAt,
        created_at: createdAt,
        updated_at: item.updated_at || createdAt
    };
}

function normalizeSavedItems(items: UserSavedItem[], scope: SavedItemRepositoryScope = {}): UserSavedItem[] {
    return items
        .filter((item) => Boolean(item.product_cluster_id))
        .map((item) => normalizeSavedItem(item, scope));
}

function shouldPersistNormalizedItems(items: UserSavedItem[], normalizedItems: UserSavedItem[]): boolean {
    return JSON.stringify(items) !== JSON.stringify(normalizedItems);
}

function filterByScope(items: UserSavedItem[], scope?: SavedItemRepositoryScope | null): UserSavedItem[] {
    return hasSavedItemScope(scope) ? items.filter((item) => matchesSavedItemScope(item, scope)) : items;
}

function scopedIdentity(identity: SavedItemIdentity, scope?: SavedItemRepositoryScope | null): SavedItemIdentity {
    const userId = scopedUserId(scope);
    const anonymousSessionId = scope ? savedItemAnonymousSessionId(scope) : null;
    if (!userId && !anonymousSessionId) return identity;
    return {
        ...identity,
        userId: savedItemUserId(identity) || userId,
        anonymousSessionId: savedItemAnonymousSessionId(identity) || anonymousSessionId
    };
}

function savedItemTime(item: UserSavedItem): number {
    return Date.parse(item.saved_at || item.created_at || item.updated_at || '') || 0;
}

export class LocalStorageSavedItemAdapter implements SavedItemAdapter {
    private readonly storageKey: string;
    private readonly legacyStorageKeys: string[];
    private readonly storage?: LookitStorage | null;
    private readonly fallbackItems: UserSavedItem[];

    constructor(options: LocalStorageSavedItemAdapterOptions = {}) {
        this.storageKey = options.storageKey || savedItemsStorageKey;
        this.legacyStorageKeys = options.legacyStorageKeys || savedItemsLegacyStorageKeys;
        this.storage = options.storage;
        this.fallbackItems = options.fallbackItems || lookitUserSavedItems;
    }

    async list(params: SavedItemListParams = {}): Promise<UserSavedItem[]> {
        return cloneData(filterByScope(this.readAll(), params));
    }

    async save(item: SavedItem, params: SavedItemRepositoryScope = {}): Promise<UserSavedItem> {
        const normalizedItem = normalizeSavedItem(item, params);
        const nextItems = [
            normalizedItem,
            ...this.readAll().filter((currentItem) => (
                currentItem.id !== normalizedItem.id &&
                !(currentItem.user_id === normalizedItem.user_id && savedItemIdentityKey(currentItem) === savedItemIdentityKey(normalizedItem))
            ))
        ];

        this.writeAll(nextItems);
        return cloneData(normalizedItem);
    }

    async remove(identity: SavedItemIdentity, params: SavedItemRepositoryScope = {}): Promise<UserSavedItem[]> {
        const scoped = scopedIdentity(identity, params);
        const nextItems = this.readAll().filter((item) => !matchesSavedItemIdentity(item, scoped));
        const savedItems = this.writeAll(nextItems);
        return cloneData(filterByScope(savedItems, scoped));
    }

    async updateAlertPreference(input: SavedItemAlertPreferenceInput, params: SavedItemRepositoryScope = {}): Promise<UserSavedItem | null> {
        const now = new Date().toISOString();
        const scoped = scopedIdentity(input, params);
        const alertPreference = input.alertPreference ?? input.alert_preference ?? null;
        const nextItems = this.readAll().map((item) => {
            if (!matchesSavedItemIdentity(item, scoped)) return item;
            return {
                ...item,
                alert_preference: alertPreference,
                updated_at: now
            };
        });
        const savedItems = this.writeAll(nextItems);
        return cloneData(savedItems.find((item) => matchesSavedItemIdentity(item, scoped)) || null);
    }

    async mergeAnonymousToUser(params: SavedItemAnonymousMergeParams): Promise<UserSavedItem[]> {
        const anonymousSessionId = params.anonymousSessionId || params.anonymous_session_id || null;
        const userId = params.userId || params.user_id || null;
        if (!anonymousSessionId || !userId) return [];

        const now = params.mergedAt || new Date().toISOString();
        const strategy = params.conflictStrategy || 'latest_saved';
        const anonymousUserId = anonymousSessionUserId(anonymousSessionId);
        const items = this.readAll();
        const mergedUserItems = new Map<string, UserSavedItem>();
        const retainedItems: UserSavedItem[] = [];

        items.forEach((item) => {
            const itemAnonymousSessionId = item.anonymous_session_id || anonymousSessionIdFromUserId(item.user_id);
            const isAnonymousItem = item.user_id === anonymousUserId || itemAnonymousSessionId === anonymousSessionId;
            const isTargetUserItem = item.user_id === userId;

            if (!isAnonymousItem && !isTargetUserItem) {
                retainedItems.push(item);
                return;
            }

            const userScopedItem = normalizeSavedItem({
                ...item,
                user_id: userId,
                owner_type: 'user',
                anonymous_session_id: null,
                updated_at: now
            }, { userId, ownerType: 'user' });
            const key = savedItemIdentityKey(userScopedItem);
            const currentItem = mergedUserItems.get(key);

            if (!currentItem) {
                mergedUserItems.set(key, userScopedItem);
                return;
            }

            if (strategy === 'prefer_anonymous' && isAnonymousItem) {
                mergedUserItems.set(key, userScopedItem);
                return;
            }

            if (strategy === 'latest_saved' && savedItemTime(userScopedItem) >= savedItemTime(currentItem)) {
                mergedUserItems.set(key, userScopedItem);
            }
        });

        const savedItems = this.writeAll([...Array.from(mergedUserItems.values()), ...retainedItems]);
        return cloneData(filterByScope(savedItems, { userId }));
    }

    async listSavedItems(params: SavedItemListParams = {}): Promise<UserSavedItem[]> {
        return this.list(params);
    }

    async saveItem(item: SavedItem): Promise<UserSavedItem> {
        return this.save(item);
    }

    async removeSavedItem(identity: SavedItemIdentity): Promise<UserSavedItem[]> {
        return this.remove(identity);
    }

    async updateSavedItemNote(input: SavedItemNoteUpdate): Promise<UserSavedItem | null> {
        const now = new Date().toISOString();
        const nextItems = this.readAll().map((item) => {
            if (!matchesSavedItemIdentity(item, input)) return item;
            return {
                ...item,
                note: input.note,
                updated_at: now
            };
        });
        const savedItems = this.writeAll(nextItems);
        return cloneData(savedItems.find((item) => matchesSavedItemIdentity(item, input)) || null);
    }

    async replaceSavedItems(items: UserSavedItem[], params: SavedItemRepositoryScope = {}): Promise<UserSavedItem[]> {
        const normalizedItems = normalizeSavedItems(items, params);

        if (!hasSavedItemScope(params)) {
            return cloneData(this.writeAll(normalizedItems));
        }

        const retainedItems = this.readAll().filter((item) => !matchesSavedItemScope(item, params));
        const savedItems = this.writeAll([...normalizedItems, ...retainedItems]);
        return cloneData(filterByScope(savedItems, params));
    }

    private readAll(): UserSavedItem[] {
        const items = readLocalArray<UserSavedItem>(
            this.storageKey,
            this.fallbackItems,
            this.storage,
            this.legacyStorageKeys
        );
        const normalizedItems = normalizeSavedItems(items);
        const resolvedStorage = browserStorage(this.storage);

        if (resolvedStorage && shouldPersistNormalizedItems(items, normalizedItems)) {
            resolvedStorage.setItem(this.storageKey, JSON.stringify(normalizedItems));
        }

        return normalizedItems;
    }

    private writeAll(items: UserSavedItem[]): UserSavedItem[] {
        const normalizedItems = normalizeSavedItems(cloneData(items));
        const resolvedStorage = browserStorage(this.storage);

        if (resolvedStorage) {
            resolvedStorage.setItem(this.storageKey, JSON.stringify(normalizedItems));
        }

        return normalizedItems;
    }
}

let activeSavedItemAdapter: SavedItemAdapter | null = null;

export function createLocalStorageSavedItemAdapter(options: LocalStorageSavedItemAdapterOptions = {}): SavedItemAdapter {
    return new LocalStorageSavedItemAdapter(options);
}

export function useSavedItemAdapter(adapter: SavedItemAdapter | null): void {
    activeSavedItemAdapter = adapter;
}

function resolveSavedItemAdapter(options: SavedItemServiceOptions = {}): SavedItemAdapter {
    return options.adapter || activeSavedItemAdapter || createLocalStorageSavedItemAdapter(options);
}

export async function listSavedItems(options: SavedItemServiceOptions = {}): Promise<UserSavedItem[]> {
    return resolveSavedItemAdapter(options).list({
        userId: options.userId,
        anonymousSessionId: options.anonymousSessionId,
        ownerType: options.ownerType
    });
}

export async function saveItem(item: SavedItemRepositorySaveInput, options: SavedItemServiceOptions = {}): Promise<UserSavedItem> {
    return resolveSavedItemAdapter(options).save(item, options);
}

export async function removeSavedItem(identity: SavedItemIdentity, options: SavedItemServiceOptions = {}): Promise<UserSavedItem[]> {
    return resolveSavedItemAdapter(options).remove(scopedIdentity(identity, options), options);
}

export async function updateSavedItemNote(input: SavedItemNoteUpdate, options: SavedItemServiceOptions = {}): Promise<UserSavedItem | null> {
    return resolveSavedItemAdapter(options).updateSavedItemNote(scopedIdentity(input, options) as SavedItemNoteUpdate);
}

export async function updateSavedItemAlertPreference(
    input: SavedItemAlertPreferenceInput,
    options: SavedItemServiceOptions = {}
): Promise<UserSavedItem | null> {
    return resolveSavedItemAdapter(options).updateAlertPreference(scopedIdentity(input, options) as SavedItemAlertPreferenceInput, options);
}

export async function mergeAnonymousSavedItemsToUser(
    params: SavedItemAnonymousMergeParams,
    options: SavedItemServiceOptions = {}
): Promise<UserSavedItem[]> {
    return resolveSavedItemAdapter(options).mergeAnonymousToUser(params);
}

export async function saveSavedItems(items: UserSavedItem[], options: SavedItemServiceOptions = {}): Promise<UserSavedItem[]> {
    return resolveSavedItemAdapter(options).replaceSavedItems(items, options);
}

export const savedItemService = {
    storageKey: savedItemsStorageKey,
    legacyStorageKeys: savedItemsLegacyStorageKeys,
    createLocalStorageAdapter: createLocalStorageSavedItemAdapter,
    useAdapter: useSavedItemAdapter,
    listSavedItems,
    saveItem,
    removeSavedItem,
    updateAlertPreference: updateSavedItemAlertPreference,
    updateSavedItemNote,
    mergeAnonymousToUser: mergeAnonymousSavedItemsToUser,
    saveSavedItems
};
